import React from 'react';
import connectToStores from 'alt-utils/lib/connectToStores';
import {Moment} from 'moment';
import {Map, Set} from 'immutable';
import NineBoxDataExportButton from '@components/nine-box/data-export/NineBoxDataExportButton';
import {MetricAxis, MetricData, MetricInfo} from '@store/@types/metric';
import NineBoxMetricSource, {FetchMetricReq} from '@store/metrics-trends/NineBoxMetricSource';
import {apiDateFormat} from 'utils/const';
import WebDataRocksActions, {
  WebDataType,
  xlsxDateFormat,
} from '@store/web-data-rocks/WebDataRocksActions';
import User, {UserId} from 'records/User';
import {List} from 'immutable';
import NineBoxMetricStore from '@store/metrics-trends/NineBoxMetricStore';
import NineBoxMetricActions from '@store/metrics-trends/NineBoxMetricActions';
import {TeamId} from 'records/TeamId';

interface NineBoxDataExportButtonContainerProps {
  readonly startDate: Moment;
  readonly endDate: Moment;
  readonly users: Map<UserId, User>;
  // dashboard team ids.
  readonly teams?: Set<TeamId>;
  // optional currently-selected rep.
  readonly selectedRep?: User;
  // Options available to export
  readonly exportOptions: List<MetricInfo>;
}

interface ExportedMetricData {
  readonly Email: string;
  readonly Date: string;
  readonly Metric: string;
  readonly Value: number;
}

/**
 * Wires up the nine box export button to the appropriate source and store.
 */
@connectToStores
class NineBoxDataExportButtonContainer extends React.Component<
  NineBoxDataExportButtonContainerProps &
    ReturnType<typeof NineBoxDataExportButtonContainer.getPropsFromStores>
> {
  static getStores() {
    return [NineBoxMetricStore];
  }

  static getPropsFromStores({exportOptions}: NineBoxDataExportButtonContainerProps) {
    const {exportSelections, horizontal, vertical} = NineBoxMetricStore.getState();
    const axisSelections = exportOptions.filter(
      opt => horizontal.metric.equals(opt.value) || vertical.metric.equals(opt.value)
    );
    return {exportSelections, axisSelections};
  }

  render() {
    const {
      startDate,
      endDate,
      users,
      teams,
      selectedRep,
      exportOptions,
      exportSelections,
      axisSelections,
    } = this.props;
    const performExport = async () => {
      const start = startDate.format(apiDateFormat);
      const end = endDate.format(apiDateFormat);
      const toExport = exportOptions
        .filter(opt => {
          const selected = exportSelections.get(opt.value);
          if (selected === false) return false; // User marked as do not export
          if (selected === true) return true; // User marked as export
          // User didn't click select
          // If default metric wasn't unchecked, then include those
          return !!axisSelections.find(s => s.value.equals(opt.value));
        })
        .toJS();

      const promises = toExport.map(requestedMetric => {
        const request: FetchMetricReq = {
          startDate: start,
          endDate: end,
          metric: requestedMetric.value.metric,
          direction: requestedMetric.value.direction,
          teams: teams?.toJS(),
          metricAxis: MetricAxis.HORIZONTAL, // do not care what the axis is here.
          context: requestedMetric.value.context,
        };
        // Call source directly to avoid interfering with the metrics in the store, being used by nine box.
        return NineBoxMetricSource.fetchMetric(request).then(result => result.data);
      });
      const trends = await Promise.all(promises);
      const data = buildData(users, toExport, trends, selectedRep);
      // Dialog needs to be visible before data is set, so we need to show, then defer the setData call.
      WebDataRocksActions.showDialog('Metrics');
      setTimeout(() => WebDataRocksActions.setData(WebDataType.NINE_BOX_METRICS, data));
    };
    return (
      <NineBoxDataExportButton
        exportOptions={exportOptions}
        performExport={performExport}
        axisSelections={axisSelections}
        exportSelections={exportSelections}
        toggleExport={NineBoxMetricActions.toggleExport}
      />
    );
  }
}

/** Build flexmonster data for export. */
function buildData(
  users: Map<UserId, User>,
  requestedMetrics: MetricInfo[],
  trends: MetricData[],
  selectedRep?: User
): ExportedMetricData[] {
  const emailByUserId = users.map(e => e.email);
  const result: any[] = [];
  trends.forEach((trend, idx) => {
    // trend is an object with userId keys...
    for (const userId in trend) {
      if (selectedRep && selectedRep.userId !== userId) {
        // If a single rep has been selected, skip other reps.
        continue;
      }
      const commonValues = {
        Email: emailByUserId.get(userId),
        Metric: requestedMetrics[idx].title,
      };
      // ...and the value is an object containing the array of data points (the trend line).
      const dataPoints = trend[userId]?.data ?? [];
      dataPoints.forEach(dataPoint => {
        const newRow = {
          ...commonValues,
          Date: dataPoint.date.format(xlsxDateFormat),
          Value: dataPoint.value,
        };
        result.push(newRow);
      });
    }
  });
  return result;
}

export default NineBoxDataExportButtonContainer as unknown as React.FC<NineBoxDataExportButtonContainerProps>;
